import { useState, useEffect } from 'react'
import { listAnnouncements } from '../services/announcement-api'
import type { Announcement } from '../types/announcement.types'

interface UseAnnouncementsOptions {
  includeInactive?: boolean
}

interface UseAnnouncementsState {
  announcements: Announcement[]
  loading: boolean
  error: Error | null
  refresh: () => void
}

export function useAnnouncements(options?: UseAnnouncementsOptions): UseAnnouncementsState {
  const includeInactive = options?.includeInactive ?? false
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    let cancelled = false

    const fetchAnnouncements = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await listAnnouncements({ includeInactive })
        if (!cancelled) {
          setAnnouncements(data)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch announcements'))
          setLoading(false)
        }
      }
    }

    fetchAnnouncements()

    return () => {
      cancelled = true
    }
  }, [refreshKey, includeInactive])

  const refresh = () => {
    setRefreshKey(prev => prev + 1)
  }

  return { announcements, loading, error, refresh }
}
